import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getCoach, getPlayerProfile, getCoachByUsername, getUserMembershipCredits } from '../services/firestoreService';
import { useAuth } from './AuthContext';
import { resolveUserTier } from '../services/accessResolver';
import { USER_TIERS } from '../constants/accessLevels';

/**
 * CoachContext - Provides coach data to all coaching pages
 *
 * This context:
 * - Fetches coach data based on username URL parameter (primary) or coachId (legacy)
 * - Resolves username → customUrls → profileId → playerProfile → coachId → coach
 * - Fetches the linked playerProfile for Stripe/premium tier info
 * - Provides loading and error states
 * - Makes coach data available to all GroupChess pages
 * - Exposes refetchCoach() for manual refresh after updates
 * - Fetches membership details for the current user
 */
const CoachContext = createContext(null);

export const CoachProvider = ({ children, username: usernameProp }) => {
  // Support both username (new) and coachId (legacy) params
  const { username: usernameParam, coachId: directCoachId } = useParams();
  const username = usernameProp || usernameParam;
  const { currentUser } = useAuth();
  const [coach, setCoach] = useState(null);
  const [playerProfile, setPlayerProfile] = useState(null);
  const [resolvedCoachId, setResolvedCoachId] = useState(null);
  const [resolvedUsername, setResolvedUsername] = useState(null);
  const [userTier, setUserTier] = useState(USER_TIERS.VISITOR);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Membership state
  const [membershipCredits, setMembershipCredits] = useState(0);
  const [membershipCurrentPeriodEnd, setMembershipCurrentPeriodEnd] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [freeTrialClaimed, setFreeTrialClaimed] = useState(false);

  const fetchCoachData = useCallback(async () => {
    // If no username and no direct coachId, we're on a static/hidden route
    if (!username && !directCoachId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (username) {
        // New path: resolve via username
        const result = await getCoachByUsername(username);

        if (!result) {
          setError('Coach not found');
          setLoading(false);
          return;
        }

        setCoach(result.coach);
        setPlayerProfile(result.playerProfile);
        setResolvedCoachId(result.coachId);
        setResolvedUsername(result.username);
      } else if (directCoachId) {
        // Legacy path: direct coachId
        const coachData = await getCoach(directCoachId);

        if (!coachData) {
          setError('Coach not found');
          setLoading(false);
          return;
        }

        setCoach(coachData);
        setResolvedCoachId(directCoachId);

        // Fetch linked player profile for Stripe/premium info
        if (coachData.playerProfileId) {
          const profileData = await getPlayerProfile(coachData.playerProfileId);
          setPlayerProfile(profileData);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching coach data:', err);
      setError(err.message || 'Failed to load coach data');
      setLoading(false);
    }
  }, [username, directCoachId]);

  useEffect(() => {
    fetchCoachData();
  }, [fetchCoachData]);

  // Resolve user's access tier for this coach
  useEffect(() => {
    if (loading || !resolvedCoachId || !coach?.userId) {
      setUserTier(currentUser ? USER_TIERS.AUTHENTICATED : USER_TIERS.VISITOR);
      return;
    }

    let cancelled = false;

    resolveUserTier(currentUser?.uid || null, resolvedCoachId, coach.userId)
      .then((tier) => {
        if (!cancelled) setUserTier(tier);
      })
      .catch((err) => {
        console.error('Error resolving user tier:', err);
        if (!cancelled) {
          setUserTier(currentUser ? USER_TIERS.AUTHENTICATED : USER_TIERS.VISITOR);
        }
      });

    return () => { cancelled = true; };
  }, [currentUser, resolvedCoachId, coach?.userId, loading]);

  // Fetch membership details when user and coach are resolved
  useEffect(() => {
    if (loading || !resolvedCoachId || !currentUser?.uid) {
      setMembershipCredits(0);
      setMembershipCurrentPeriodEnd(null);
      setIsMember(false);
      setFreeTrialClaimed(false);
      return;
    }

    let cancelled = false;

    getUserMembershipCredits(currentUser.uid, resolvedCoachId)
      .then((info) => {
        if (!cancelled) {
          setMembershipCredits(info.membershipCredits || 0);
          setMembershipCurrentPeriodEnd(info.membershipCurrentPeriodEnd || null);
          setIsMember(info.isMember || false);
          setFreeTrialClaimed(info.freeTrialClaimed || false);
        }
      })
      .catch((err) => {
        console.error('Error fetching membership info:', err);
      });

    return () => { cancelled = true; };
  }, [currentUser?.uid, resolvedCoachId, loading]);

  /**
   * Get the platform fee percentage based on the playerProfile's premium tier
   * Standard tier: 10% fee
   * Pro tier: 5% fee
   */
  const getPlatformFeePercent = () => {
    if (!playerProfile) return 10; // Default to standard tier

    const tier = playerProfile.premiumTier || 'standard';
    return tier === 'pro' ? 5 : 10;
  };

  /**
   * Calculate the application fee amount for Stripe checkout
   * @param {number} amount - The total amount in cents
   * @returns {number} - The application fee in cents
   */
  const getApplicationFeeAmount = (amount) => {
    const feePercent = getPlatformFeePercent();
    return Math.round(amount * (feePercent / 100));
  };

  /**
   * Check if the coach has Stripe Connect set up
   */
  const hasStripeConnect = () => {
    return !!playerProfile?.stripeAccountId;
  };

  /**
   * Get the Stripe Connect account ID
   */
  const getStripeConnectAccountId = () => {
    return playerProfile?.stripeAccountId || null;
  };

  /**
   * Get the display name (fallback to 'Coach' if not set)
   */
  const getDisplayName = () => {
    return coach?.displayName || 'Coach';
  };

  /**
   * Get package pricing with defaults
   */
  const getPackagePricing = () => {
    return coach?.packagePricing || {
      sessions1: { usd: 15, perk: '' },
      sessions4: { usd: 52, perk: '' },
      sessions7: { usd: 84, perk: '' },
    };
  };

  /**
   * Check if the coach profile is active
   */
  const isActive = () => {
    return coach?.status === 'active';
  };

  const isOwner = () => userTier === USER_TIERS.OWNER;

  /** Check if the coach has a membership offering configured */
  const hasMembership = () => coach?.membership?.enabled === true;

  /** Check if the coach has credit packs visible (defaults to ON for backward compat) */
  const hasCreditPacks = () => coach?.offers?.creditPacksPublic !== false;

  /** Get the full membership config object */
  const getMembershipConfig = () => coach?.membership || null;

  const value = {
    // Raw data
    coach,
    playerProfile,
    // coachId is the resolved Firestore ID (use this for queries)
    coachId: resolvedCoachId,
    // username is from the URL (use this for building links)
    username: resolvedUsername || username,
    loading,
    error,
    // Access control
    userTier,

    // Membership state
    isMember,
    membershipCredits,
    membershipCurrentPeriodEnd,
    freeTrialClaimed,

    // Helper functions
    getPlatformFeePercent,
    getApplicationFeeAmount,
    hasStripeConnect,
    getStripeConnectAccountId,
    getDisplayName,
    getPackagePricing,
    isActive,
    isOwner,
    hasMembership,
    hasCreditPacks,
    getMembershipConfig,
    refetchCoach: fetchCoachData,
  };

  return (
    <CoachContext.Provider value={value}>
      {children}
    </CoachContext.Provider>
  );
};

/**
 * Hook to access coach context
 * @returns {Object} Coach context value
 */
export const useCoach = () => {
  const context = useContext(CoachContext);
  if (context === undefined) {
    throw new Error('useCoach must be used within a CoachProvider');
  }
  return context;
};

export default CoachContext;
